import { useState, useEffect, useLayoutEffect, useRef, useContext } from "react";
import { createPortal } from "react-dom";
import { translate } from "../utils/Common";
import { usePlatforms } from "../hooks/usePlatforms";
import AuthContext from "../contexts/auth";
import Spinner from "./Spinner";
import { AiOutlineClose } from "react-icons/ai";
import {  PiLockersFill } from "react-icons/pi";
import ignoreAbort from '../utils/ignoreAbort';
import { SelectShell } from "@/components/ui/select-shell";
import { FIELD_SKIN } from "@/components/ui/form-field";
import { cn } from "@/lib/utils";

// Separación entre el campo y el menú, en px
const GAP = 6;

const PlatformSearchList = ({
  register,
  registerRef,
  errors,
  nameShow,
  idShow,
  readOnly,
  setValue,
  valueId,
  setPlatformSelect,
  platfSelect,
  idPlatform,
  handleIsBoxit,
  required,
  defaultPlatformId,
  idsToHide=[],
  isMultiSelect = false,
  selectedPlatforms = [],
  setSelectedPlatforms,
  isClearable = false
}) => {
  const selectRegisterProps = registerRef || (register && valueId ? register(valueId) : {});
  const userContext = useContext(AuthContext);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [openSelect, setOpenSelect] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef(null);
  const menuRef = useRef(null);
  // El menú se renderiza en un portal con posición fija, no como `absolute` dentro
  // del campo: sus ancestros recortan (`.gridItem` de Insights y `ContentCard` traen
  // `overflow: hidden` para contener el ancho de los gráficos y las esquinas
  // redondeadas), así que dentro del árbol quedaba cortado por el borde de la tarjeta.
  // `null` = todavía sin medir; el menú se monta invisible para poder medirlo.
  const [menuPos, setMenuPos] = useState(null);
  const { platforms, getPlatforms } = usePlatforms();
  const [plataformasList, setPlataformasList] = useState(null);

  useEffect(() => {
    const abortController = new AbortController();

    const handlePlatforms = async () => {
        const platforms = await getPlatforms(userContext.user?.IdCliente, abortController.signal);
        setPlataformasList(platforms);
    }

    if (!platforms) handlePlatforms().catch(ignoreAbort)
    else setPlataformasList(platforms)
    
    return () => abortController.abort();
}, [platforms, getPlatforms, userContext.user?.IdCliente]);

useEffect(() => {
  if (idPlatform !== translate("global.select") && idPlatform !== translate("global.noPlatforms")) {
      const isBoxitSelected = platforms?.filter(p => p.boxitLocationId === Number(idPlatform)) && platforms?.filter(p => p.boxitLocationId === Number(idPlatform))[0]?.IsBoxit;
      if (handleIsBoxit) handleIsBoxit(isBoxitSelected);
  }
}, [idPlatform, handleIsBoxit, platforms])

  useEffect(() => {
    const handleOutsideClick = (event) => {
      // El menú está en un portal, así que no es descendiente de wrapperRef: sin
      // esta segunda comprobación, el mousedown sobre una opción cerraría el menú
      // antes de que llegue su click y no se podría seleccionar nada.
      const insideField = wrapperRef.current?.contains(event.target);
      const insideMenu = menuRef.current?.contains(event.target);
      if (!insideField && !insideMenu) {
        setOpenSelect(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [wrapperRef]);

   // Este efecto se encarga de establecer la plataforma seleccionada por defecto
   useEffect(() => {

    if (defaultPlatformId && plataformasList) {

      if (plataformasList.length > 0) {

        const defaultPlatform = plataformasList.find(p => {
          return p.boxitLocationId === Number(defaultPlatformId)
        });

        if (defaultPlatform) {
          handleProviderSelection(defaultPlatform)
          setOpenSelect(false)
        }
      }
    
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultPlatformId, plataformasList]);


  const toggleSelect = () => {
    setOpenSelect((prevOpenSelect) => !prevOpenSelect);
    setSearchTerm("");
  };

  // Se mide el menú ya renderizado (su alto depende del contenido) y se coloca contra
  // el campo. En useLayoutEffect para que ocurra antes del primer pintado, así no se ve
  // aparecer en la esquina ni saltar de dirección. No depende de searchTerm a propósito:
  // recalcular mientras se escribe movería el menú cada vez que la lista cambia de alto.
  useLayoutEffect(() => {
    if (!openSelect) {
      setMenuPos(null);
      return;
    }

    const placeMenu = () => {
      const field = wrapperRef.current?.getBoundingClientRect();
      if (!field) return;
      const menuHeight = menuRef.current?.offsetHeight ?? 0;

      const spaceBelow = window.innerHeight - field.bottom;
      const spaceAbove = field.top;
      // Hacia arriba solo si abajo no cabe y arriba hay más espacio; si no cabe en
      // ningún lado se queda abajo, donde la lista ya scrollea sola.
      const dropUp = menuHeight > 0 && spaceBelow < menuHeight + GAP && spaceAbove > spaceBelow;

      setMenuPos({
        left: field.left,
        width: field.width,
        ...(dropUp
          ? { bottom: window.innerHeight - field.top + GAP }
          : { top: field.bottom + GAP }),
      });
    };

    placeMenu();
    // `true` para capturar el scroll de cualquier contenedor, no solo el de la ventana
    window.addEventListener('resize', placeMenu);
    window.addEventListener('scroll', placeMenu, true);
    return () => {
      window.removeEventListener('resize', placeMenu);
      window.removeEventListener('scroll', placeMenu, true);
    };
  }, [openSelect]);

  const handleProviderSelection = (selectedValue) => {
    if (isMultiSelect) {
      // Si ya está seleccionado, lo eliminamos; si no, lo agregamos
      setSelectedPlatforms && setSelectedPlatforms((prev) => {
        const alreadySelected = prev.some(
          (p) => p.boxitLocationId === selectedValue.boxitLocationId
        );
        if (alreadySelected) {
          return prev.filter(
            (p) => p.boxitLocationId !== selectedValue.boxitLocationId
          );
        } else {
          return [...prev, selectedValue];
        }
      });
    } else {
      setSelectedPlatform(selectedValue.boxitLocationId);
      setValue(valueId, selectedValue.boxitLocationId);
      setPlatformSelect && setPlatformSelect(selectedValue);
      toggleSelect();
    }
  };
  
  // Función para seleccionar todos
const handleSelectAll = () => {
  if (selectedPlatforms.length === filteredProviders.length) {
    setSelectedPlatforms && setSelectedPlatforms([]); // Deseleccionar todos
  } else {
    setSelectedPlatforms && setSelectedPlatforms(filteredProviders); // Seleccionar todos
  }
};

  const filteredProviders = plataformasList
  ? plataformasList.filter(
      (prov) =>
        idShow !== prov.boxitLocationId &&
        !idsToHide.includes(prov.boxitLocationId) && // Aquí añadimos la condición
        `${prov.locationName}`.toLowerCase().includes(searchTerm.toLowerCase())
    )
  : [];

  const handleClearSelection = () => {
    setSelectedPlatform(null);
    setValue(valueId, "");
    setPlatformSelect && setPlatformSelect(null);
  };


  const buttonLabel = isMultiSelect
    ? selectedPlatforms.length > 0
      ? `${selectedPlatforms.length} ${translate("global.selected")}`
      : `${translate("global.select")} Boxit`
    : (platfSelect &&
        plataformasList?.find((item) => item.boxitLocationId === selectedPlatform)?.locationName) ||
      `${translate("global.select")} Boxit`;

  const hasSelection = isMultiSelect ? selectedPlatforms.length > 0 : Boolean(selectedPlatform);

  return (
    <div ref={wrapperRef} className="relative">
      <SelectShell
        icon={<PiLockersFill size={16} />}
        error={errors?.[valueId] || errors?.proveedor}
      >
        {/* A button, not a `select` with `pointer-events: none` sitting on top of
            a hidden one. The registered value travels in a hidden input, so
            react-hook-form sees exactly what it saw before. */}
        <button
          type="button"
          id="platform-select-list"
          disabled={readOnly}
          onClick={toggleSelect}
          aria-haspopup="listbox"
          aria-expanded={openSelect}
          className={cn(
            FIELD_SKIN,
            'flex w-full items-center justify-between gap-2 pl-10 pr-3 text-left',
            'transition-[border-color,box-shadow] disabled:cursor-not-allowed',
            !hasSelection && 'text-muted-foreground/70'
          )}
        >
          <span className="truncate">{buttonLabel}</span>
          {isClearable && hasSelection && (
            <span
              role="button"
              tabIndex={0}
              aria-label={translate("global.clean")}
              onClick={(e) => { e.stopPropagation(); handleClearSelection(e); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); handleClearSelection(e); } }}
              className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground"
            >
              <AiOutlineClose size={14} />
            </span>
          )}
        </button>
      </SelectShell>

      <input type="hidden" {...selectRegisterProps} required={required} value={selectedPlatform ?? ''} readOnly />

      {openSelect && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            left: menuPos?.left ?? 0,
            width: menuPos?.width,
            top: menuPos?.top,
            bottom: menuPos?.bottom,
            // Mismo nivel que los menús de react-select portalizados de la app
            // (reactSelectThemeStyles: menuPortal zIndex 9999), para que todos los
            // pickers se apilen igual. Queda por debajo de los modales (10000+), que
            // hoy no contienen este campo.
            zIndex: 9999,
            // Se monta invisible para medirlo; useLayoutEffect lo coloca antes de pintar
            visibility: menuPos ? 'visible' : 'hidden',
          }}
          className="overflow-hidden rounded-lg border border-border bg-popover shadow-lg animate-fade-in"
        >
          <div className="space-y-2 p-2">
            <input
              type="text"
              className={cn(FIELD_SKIN, 'h-9 w-full px-3')}
              placeholder={translate("global.search")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {isMultiSelect && filteredProviders.length > 0 && (
              <button
                type="button"
                onClick={handleSelectAll}
                className="w-full rounded-md px-2 py-1.5 text-xs font-medium text-primary-strong transition-colors hover:bg-accent"
              >
                {selectedPlatforms.length === filteredProviders.length
                  ? translate("global.deselectAll")
                  : translate("global.selectAll")}
              </button>
            )}
          </div>

          <ul role="listbox" className="max-h-64 list-none overflow-y-auto p-1.5 pt-0">
            {filteredProviders.length > 0 ? (
              filteredProviders.map((prov) => {
                const selected = isMultiSelect
                  ? selectedPlatforms.some((p) => p.boxitLocationId === prov.boxitLocationId)
                  : selectedPlatform === prov.boxitLocationId;
                return (
                  <li key={prov.boxitLocationId} role="option" aria-selected={selected}>
                    <button
                      type="button"
                      onClick={() => handleProviderSelection(prov)}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm transition-colors',
                        selected
                          ? 'bg-primary text-primary-foreground'
                          : 'text-foreground hover:bg-accent'
                      )}
                    >
                      {isMultiSelect && (
                        <input
                          type="checkbox"
                          checked={selected}
                          readOnly
                          tabIndex={-1}
                          className="size-3.5 shrink-0 accent-primary"
                        />
                      )}
                      <span className="truncate">{prov.locationName}</span>
                    </button>
                  </li>
                );
              })
            ) : (
              <li className="px-3 py-3 text-sm text-muted-foreground">
                {plataformasList ? (
                  <span>
                    {translate("global.noPlatforms")} {translate("global.withTheTerm")} "{searchTerm}"
                  </span>
                ) : (
                  <Spinner type={1} size={15} />
                )}
              </li>
            )}
          </ul>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PlatformSearchList;
